import { AdminQueryUser } from '../../api/admin';
import { ADMIN_USER_POOL, IS_MASTER } from '../../constants';

export const ADMIN_URL = '/ziggy';
export const ADMIN_QUERY_PARAM = 'q';

// ## Cognito

/**
 * Get a URL for the AWS Cognito Admin page for a specific user
 *
 * Example:
 * https://us-east-1.console.aws.amazon.com/cognito/v2/idp/user-pools/us-east-1_sVZil2AFi/user-management/users/details/b8b0b1ca-bfb8-49df-9b31-3004875003c8?region=us-east-1
 */
export const getAdminCognitoUrl = (cognitoSub: string) => {
  return `https://us-east-1.console.aws.amazon.com/cognito/v2/idp/user-pools/${ADMIN_USER_POOL}/user-management/users/details/${encodeURIComponent(cognitoSub)}?region=us-east-1`;
};

// ## Dynamo

/**
 * Get a URL for the AWS DynamoDB admin page for the users table
 *
 * Example:
 * https://us-east-1.console.aws.amazon.com/dynamodbv2/home?region=us-east-1#edit-item?itemMode=2&pk=us-east-1%3A77d001b6-ddba-4f62-ad8b-c0ba34eb863e&route=ROUTE_ITEM_EXPLORER&sk=main&table=fpusers-dev
 */
export const getAdminDynamoUserUrl = (userId: string) =>
  _getDynamoUrl({ tableBase: 'fpusers', pk: userId });

export const getAdminDynamoOrgUrl = (orgId: string) =>
  _getDynamoUrl({ tableBase: 'fporgs', pk: orgId });

const _getDynamoUrl = ({
  tableBase,
  pk,
}: {
  tableBase: string;
  pk: string;
}) => {
  // NOTE - cannot link to list page, won't auto-search, so deep link to record

  const params = new URLSearchParams();

  params.set('itemMode', '2');
  params.set('pk', pk);
  params.set('route', 'ROUTE_ITEM_EXPLORER');
  params.set('sk', 'main');
  params.set('table', `${tableBase}-${IS_MASTER ? 'master' : 'dev'}`);
  return `https://us-east-1.console.aws.amazon.com/dynamodbv2/home?region=us-east-1#edit-item?${params.toString()}`;
};

/**
 * Example:
 * https://us-east-1.console.aws.amazon.com/dynamodbv2/home?region=us-east-1#item-explorer?index=orgMembers&maximize=true&operation=QUERY&pk=c2775a2f-ad6e-41a5-a330-5f69e7510f7d&table=fpusers-dev
 */
export const getAdminDynamoOrgUsersUrl = (orgId: string) => {
  const tableBase = 'fpusers';
  const parmasDict = {
    index: 'orgMembers',
    maximize: 'true',
    operation: 'QUERY',
    pk: orgId,
    table: `${tableBase}-${IS_MASTER ? 'master' : 'dev'}`,
  };

  const params = new URLSearchParams();
  Object.entries(parmasDict).forEach(([k, v]) => {
    params.set(k, v);
  });

  return `https://us-east-1.console.aws.amazon.com/dynamodbv2/home?region=us-east-1#item-explorer?${params.toString()}`;
};

// ## Stripe

/**
 * Get a URL for the Stripe Dashboard for a customer
 *
 * Example:
 * https://dashboard.stripe.com/test/customers/cus_S8dG38ewc5Vy6q
 */
export const getAdminStripeCustomerUrl = (customerId: string) =>
  _getStripeUrl({ resource: 'customers', id: customerId });

const _getStripeUrl = ({ resource, id }: { resource: string; id: string }) =>
  `https://dashboard.stripe.com/${IS_MASTER ? '' : 'test/'}${encodeURIComponent(resource)}/${encodeURIComponent(id)}`;

// ## Admin data url mappers

type UrlMapper = Partial<Record<string, (key: string) => string>>;

const _DB_MAPPERS: UrlMapper = {
  customerId: getAdminStripeCustomerUrl,
  orgId: getAdminDynamoOrgUrl,
  sub: getAdminCognitoUrl,
  userId: getAdminDynamoUserUrl,
  planId: (val) => _getStripeUrl({ resource: 'prices', id: val }),
  subscriptionId: (val) =>
    _getStripeUrl({ resource: 'subscriptions', id: val }),
};

/** mapping of admin data to external URLs */
export const ADMIN_DATA_URL_MAPPERS: Record<keyof AdminQueryUser, UrlMapper> = {
  cognito: {
    'attrs.sub': getAdminCognitoUrl,
    'attrs.custom:identity_id': getAdminDynamoUserUrl,
  },
  dbUser: {
    ..._DB_MAPPERS,
  },
  dbOrg: {
    ..._DB_MAPPERS,
  },
  stripeCustomer: {
    id: getAdminStripeCustomerUrl,
  },
};

const _getAdminQueryUrl = (query: string) => {
  return `${ADMIN_URL}?${ADMIN_QUERY_PARAM}=${encodeURIComponent(query)}`;
};

/** mapping of admin data to local link `to` values */
export const ADMIN_DATA_LINK_MAPPERS: Record<keyof AdminQueryUser, UrlMapper> =
  {
    cognito: {},
    dbUser: {
      orgId: _getAdminQueryUrl,
    },
    dbOrg: {
      adminEmail: _getAdminQueryUrl,
    },
    stripeCustomer: {},
  };

const DT_FORMAT_FULL: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

const _tsToDatetime = (timestamp: unknown) => {
  if (typeof timestamp === 'number') {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString(undefined, DT_FORMAT_FULL);
  }
  return String(timestamp);
};

const _DB_FORMAT_MAPPERS: Partial<Record<string, (key: unknown) => string>> = {
  prevSubLastCreated: _tsToDatetime,
  created: _tsToDatetime,
  updated: _tsToDatetime,
  'emailLog[].created': _tsToDatetime,
  current_period_start: _tsToDatetime,
  current_period_end: _tsToDatetime,
  amount: (val) => {
    if (typeof val === 'number') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(val / 100);
    }
    return String(val);
  },
};

export const ADMIN_DATA_FORMAT_MAPPERS: Record<
  keyof AdminQueryUser,
  Partial<Record<string, (key: unknown) => string>>
> = {
  cognito: {
    // 'attrs.sub': getAdminCognitoUrl,
    // 'attrs.custom:identity_id': getAdminDynamoUserUrl,
  },
  dbUser: {
    ..._DB_FORMAT_MAPPERS,
  },
  dbOrg: {
    ..._DB_FORMAT_MAPPERS,
  },
  stripeCustomer: {
    // id: getAdminStripeCustomerUrl,
  },
};
